import { useState } from 'react';
import Draggable from 'react-draggable';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ReactComponent as Expand } from 'src/assets/tailwind/expand-icon.svg';
import { ReactComponent as Pause } from 'src/assets/tailwind/pause-icon.svg';
import { ReactComponent as PlayArrow } from 'src/assets/tailwind/play.svg';
import { ReactComponent as Stop } from 'src/assets/tailwind/stop-icon.svg';
import { ReactComponent as CloseIcon } from 'src/assets/tailwind/x-close.svg';
import { Box } from 'src/components/ui/box';
import { Button } from 'src/components/ui/button';
import { ConfirmationDialog } from 'src/components/ui/ConfirmationDialog';
import { APPS_NAV_LINK } from 'src/content/MiniExperts/constants';
import Recorder from 'src/content/MiniExperts/SmartMeet/components/Recorder/index';
import { useCreateMeetHandler } from 'src/content/MiniExperts/SmartMeet/hooks/useCreateMeetHandler';
import { useSmartMeetService } from 'src/content/MiniExperts/SmartMeet/hooks/useSmartMeetService';
import {
  getAudioDuration,
  getAudioFile
} from 'src/content/MiniExperts/SmartMeet/utils/utils';
import useAnalytics from 'src/hooks/useAnalytics';
import { useMicrophonePermission } from 'src/hooks/useMicrophonePermission';
import {
  maximizeRecording,
  pauseRecording,
  RecordingProgress,
  resumeRecording,
  setDraftMeeting,
  setNotes,
  setProgress,
  setSeconds,
  stopRecordingAsync
} from 'src/redux/slices/smartMeet';
import { setAlert } from 'src/redux/slices/snackbar';
import { useDispatch, useSelector } from 'src/redux/store';
import { ALERT } from 'src/types/enum';
import {
  SMART_MEET_MINIMIZED_RECORDING_PAUSED,
  SMART_MEET_MINIMIZED_RECORDING_RESUMED,
  SMART_MEET_MINIMIZED_RECORDING_STOPPED
} from 'src/utils/analyticsUtils';
import logger from 'src/utils/logger';
import { deleteMeetingData, getMeetingMetadata } from 'src/utils/recordingDB';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';

const MinimizedRecording = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collectAnalytics = useAnalytics();
  const { uploadMeetingAudio } = useCreateMeetHandler();
  const { deleteSmartMeet } = useSmartMeetService();
  const { ensureMicrophoneAccess } = useMicrophonePermission();
  const { isRecording, isPaused, draftMeeting } = useSelector(
    (state) => state.smartMeet
  );
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const handlePauseResume = async () => {
    if (isPaused) {
      const hasAccess = await ensureMicrophoneAccess();
      if (!hasAccess) {
        return;
      }
      collectAnalytics(SMART_MEET_MINIMIZED_RECORDING_RESUMED);
      dispatch(resumeRecording());
    } else {
      collectAnalytics(SMART_MEET_MINIMIZED_RECORDING_PAUSED);
      dispatch(pauseRecording());
    }
  };

  const restoreNotesFromDB = async () => {
    if (!draftMeeting?.id || !draftMeeting?.tenant_id) return;
    try {
      const meetingName = `${draftMeeting.id}-${draftMeeting.tenant_id}`;
      const metadata = await getMeetingMetadata(meetingName);
      if (metadata?.notes?.length) {
        dispatch(setNotes(metadata.notes));
      }
    } catch (err) {
      logger.error('Error restoring notes from IndexedDB:', err);
    }
  };

  const handleMaximize = async () => {
    await restoreNotesFromDB();
    dispatch(maximizeRecording());
    navigate(APPS_NAV_LINK.SMART_MEET);
  };

  const handleStop = async () => {
    collectAnalytics(SMART_MEET_MINIMIZED_RECORDING_STOPPED);
    await restoreNotesFromDB();
    let meetingName: string | undefined;
    try {
      const result = await dispatch(stopRecordingAsync());
      try {
        meetingName = (result?.payload as { meetingName?: string })
          ?.meetingName;
      } catch (err) {
        dispatch(setAlert({ type: ALERT.ERROR, msg: t(T.stopRecordingError) }));
        logger.error('Error stopping recording:', err);
        return;
      } finally {
        dispatch(
          setProgress({
            status_code: RecordingProgress.StoppingAudio
          })
        );
      }

      const meetingId = Number(meetingName?.split('-')?.[0]);

      const audioFile = await getAudioFile(meetingName);
      const normalizedType = audioFile?.type?.split(';')?.[0];
      const duration = await getAudioDuration(audioFile);

      const uploadFile = new File([audioFile], audioFile?.name, {
        type: normalizedType,
        lastModified: Date.now()
      });
      await uploadMeetingAudio(uploadFile, {
        duration,
        fileName: draftMeeting?.name,
        meetingId
      });
    } catch (err) {
      logger.error('Error stopping recording:', err);
    }
  };

  const handlePausePlayIcon = () => (isPaused ? PlayArrow : Pause);

  const handleDiscardConfirm = async () => {
    setIsDiscardModalOpen(false);
    const meetingName = `${draftMeeting?.id}-${draftMeeting?.tenant_id}`;
    try {
      await dispatch(stopRecordingAsync());
    } catch (err) {
      logger.error('Error stopping recorder during discard:', err);
    }
    try {
      if (draftMeeting?.id) {
        await deleteSmartMeet(draftMeeting.id);
      }
    } catch (err) {
      logger.error('Error deleting draft meeting during discard:', err);
    }
    await deleteMeetingData(meetingName).catch((err) => {
      logger.error('Error deleting meeting chunks during discard:', err);
    });
    dispatch(setProgress(null));
    dispatch(setDraftMeeting(null));
    dispatch(setNotes([]));
    dispatch(setSeconds(0));
  };

  return (
    <>
      <ConfirmationDialog
        title={t(T.confirmationDialog)}
        description={t(T.discardMeetingAlert)}
        confirmText={t(T.delete)}
        cancelText={t(T.cancel)}
        variant="destructive"
        isOpen={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={handleDiscardConfirm}
      />
      <Draggable handle=".recording-minimize-handle" bounds="parent">
        <Box
          className="recording-minimize-handle tw:select-none"
          style={{
            position: 'absolute',
            background: '#175CE5',
            left: 10,
            bottom: 100,
            zIndex: 1201,
            borderRadius: 12,
            padding: '8px 12px',
            width: 216,
            color: 'white',
            boxShadow: '0 4px 16px rgba(0,0,0,0.24)'
          }}
        >
          <Box className="tw:flex tw:items-center tw:justify-between tw:mb-2">
            <span className="tw:font-semibold tw:text-bw-s-m tw:text-white tw:truncate tw:mr-2">
              {draftMeeting?.name || t(T.recording)}
            </span>
            <Button
              hierarchy="ghost"
              iconOnly
              leftIcon={Expand}
              onClick={handleMaximize}
              size="small"
              className="tw:text-icon-white tw:hover:tw:bg-transparent"
            />
          </Box>

          <Box className="tw:flex tw:items-center tw:gap-2 tw:mb-3">
            <Box className="tw:flex-1 tw:min-w-0">
              <Recorder isRecording={isRecording} mode="minimized" />
            </Box>
          </Box>

          <Box className="tw:flex tw:items-center tw:justify-end tw:gap-0.5">
            <Button
              hierarchy="ghost"
              iconOnly
              leftIcon={CloseIcon}
              onClick={() => setIsDiscardModalOpen(true)}
              size="small"
              className="tw:text-icon-white"
            />
            <Button
              hierarchy="ghost"
              iconOnly
              leftIcon={handlePausePlayIcon()}
              disabled={!isRecording}
              onClick={handlePauseResume}
              size="small"
              className="tw:text-icon-white"
            />
            <Button
              hierarchy="ghost"
              iconOnly
              leftIcon={Stop}
              disabled={!isRecording}
              onClick={handleStop}
              size="small"
              className="tw:text-icon-white"
            />
          </Box>
        </Box>
      </Draggable>
    </>
  );
};

export default MinimizedRecording;
